import { ColumnType } from 'antd/es/table';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Table } from 'components/molecules/table';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { ArrElement } from 'utils/arrays';
import { tryEmojify } from 'utils/emojify';
import { fullName } from 'utils/full-name';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { db } from 'workers';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { CpSectionWrapper } from '../common';
import { CpWidgetHeader } from '../common/header';
import { CpAccountProjectsListData } from './types';

const TableWrapper = styled.div`
  margin-top: var(--cp-spacing-06);
  max-width: 100%;
  overflow-x: auto;
`;

const renderValue = async (
  field: ArrElement<Project['fields']>,
): Promise<unknown> => {
  if (!field || !field?.fieldValue || _.isPlainObject(field?.fieldValue)) {
    return '-';
  }
  switch (field?.fieldType) {
    case FieldTypes.SingleSelect: {
      switch (field?.fieldDataType) {
        case FieldTypes.Number:
        case FieldTypes.Text:
          if (field?.metaData?.options) {
            return field?.metaData?.options?.find(
              (e) => e?.value === field?.fieldValue,
            )?.label;
          }

          return field?.fieldValue;

        case FieldTypes.Date:
          return moment(field?.fieldValue as string).format('DD MMM YYYY');

        case 'USER': {
          const val = field?.fieldValue;

          if (_.isFinite(val) && _.isNumber(val)) {
            const user = await db.members.get(val);

            if (user) {
              return fullName(user);
            }
          }

          return '-';
        }

        case FieldTypes.Boolean:
          return !!field?.fieldValue?.toString();
        case FieldTypes.MultiLineText:
          return tryGetTextFromHTML(
            tryEmojify((field?.fieldValue as string) ?? '-'),
          );

        default:
          return '';
      }
    }

    case FieldTypes.MultiSelect: {
      switch (field?.fieldDataType) {
        case FieldTypes.Number:
        case FieldTypes.Text: {
          const arr = field?.fieldValue as (string | number)[];

          return field?.metaData?.options
            ?.filter?.((e) => arr?.includes(e?.value))
            ?.map((e) => e?.label)
            ?.join?.(', ');
        }

        case 'USER': {
          const val = field?.fieldValue;

          if (_.isArray(val)) {
            const users = await db.members.where('userId').anyOf(val).toArray();

            return (users ?? [])?.map?.(fullName).join?.(', ');
          }

          return '-';
        }

        default:
          return '';
      }
    }

    case 'KEY_EVENTS':
      return moment(field?.fieldValue as string).format('DD MMM YYYY');

    case 'INTERVALS':
    case 'RATING':
      return field?.fieldValue ?? '-';

    default:
      return '-';
  }
};

const ResolvePromise: React.FC<{ promise: Promise<unknown> }> = ({
  promise,
}) => {
  const [state, setState] = useState<unknown>();

  useEffect(() => {
    (async () => {
      const val = await promise;

      if (val !== state) {
        setState(val);
      }
    })();
  }, [promise, state]);

  return <>{state ?? '-'}</>;
};

export const CpAccountProjectsList: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  disabled,
}) => {
  const background = useGetBackground(widget);
  const data = useMemo(() => _data as CpAccountProjectsListData, [_data]);

  const columns: ColumnsType<Project> = useMemo(() => {
    return (
      [
        {
          title: <FormattedMessage id="project_name" />,
          dataIndex: 'projectName',
          render: (text, p: Project) => p?.projectName,
        },
      ] as ColumnType<Project>[]
    ).concat(
      (data?.fields ?? []).map((field) => ({
        title: field.fieldLabel,
        dataIndex: field.fieldName,

        render: (text, p: Project) => {
          const fields = p?.fields ?? [];
          const fieldData = fields.find((f) => f.fieldName === field.fieldName);

          if (!fieldData) {
            return '-';
          }

          return <ResolvePromise promise={renderValue(fieldData)} />;
        },
      })),
    );
  }, [data]);

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
    >
      <CpWidgetHeader>
        <CpWidgetHeader.Title autoAdjustColor>
          {data?.title}
        </CpWidgetHeader.Title>
        {data?.description != null ? (
          <CpWidgetHeader.Description autoAdjustColor>
            {data.description}
          </CpWidgetHeader.Description>
        ) : null}
      </CpWidgetHeader>
      <TableWrapper>
        <Table
          dataSource={_.sortBy(data?.projects, (p) => p?.projectId)}
          columns={columns}
          pagination={false}
        />
      </TableWrapper>
    </CpSectionWrapper>
  );
};
